'use client';

import { useEffect, useLayoutEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import classNames from 'classnames';
import { Route } from 'next';
import ArrowUpRight from '../../../public/assets/icons/arrow-up-right.svg';
import flowBackground from '../../../public/assets/images/home/latest-post-bar-flow.webp';

/** Height of the sticky navigation the bar tucks itself under (48px). */
const NAV_OFFSET = 'top-12';

/** The Figma header fill, shared with the slot painted behind the bar. */
const BAR_GRADIENT = 'linear-gradient(to right, #EBF1FA 22.522%, #B8E8FA 100%)';
/** Total bar height: the design's 56px content box plus its 2px rule. */
const BAR_HEIGHT = 'h-[3.625rem]';

/** Scrolling down only hides the bar once the user is this far down the page. */
const HIDE_AFTER = 160;
/** Scrolling up brings it back only once the user is this close to the top. */
const SHOW_BELOW = 80;
/** Ignore sub-pixel/rubber-band scroll noise so the bar doesn't flicker. */
const DIRECTION_THRESHOLD = 4;

/** Layout effects only exist on the client; the server render skips this. */
const useBeforePaint =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * Hides the bar behind the navigation once the reader has committed to
 * scrolling down, and slides it back out when they return to the top of the
 * page. Deliberately position-aware rather than purely direction-aware: a
 * scroll-up half way down the page should not pull the bar back over the copy.
 *
 * It starts tucked away. Reloading half way down the page restores the scroll
 * position, and a bar that rendered visible would be on screen — sticky, right
 * under the navigation — until hydration pulled it back, which reads as the
 * announcement flashing up and leaving. Starting hidden means the markup the
 * browser paints before hydration is already correct, and the reveal below
 * happens before the first paint, so a reader at the top of the page never
 * sees it arrive either.
 */
const useTucksAwayOnScroll = () => {
  const [hidden, setHidden] = useState(true);
  // Until the position has been read once, the bar must not animate: the
  // opening state is a guess, and correcting it should look like nothing
  // happened rather than like a slide.
  const [settled, setSettled] = useState(false);

  useBeforePaint(() => {
    setHidden(window.scrollY > HIDE_AFTER);
    setSettled(true);
  }, []);

  useEffect(() => {
    let lastY = window.scrollY;
    let frame = 0;

    const evaluate = () => {
      frame = 0;
      const y = window.scrollY;
      const delta = y - lastY;

      if (Math.abs(delta) < DIRECTION_THRESHOLD) return;
      lastY = y;

      if (delta > 0 && y > HIDE_AFTER) setHidden(true);
      else if (delta < 0 && y < SHOW_BELOW) setHidden(false);
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(evaluate);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return { hidden, settled };
};

interface ILatestPostBarProps {
  title: string;
  slug: string;
}

/**
 * The announcement strip under the navigation pointing at the newest blog post.
 *
 * Geometry, gradient and the flow-field texture come straight from the Figma
 * header (node 10186:19532). The texture is the design's masked, colour-burned
 * bitmap flattened to the 586x56 slice the bar actually reveals, so the browser
 * only has to blend one small layer.
 *
 * It lives in the shared layout rather than in the page so its background can
 * run edge to edge like the navigation above it, and renders on the homepage
 * only — the single place the design calls for it.
 */
const LatestPostBar = ({ title, slug }: ILatestPostBarProps) => {
  const pathname = usePathname();
  const { hidden, settled } = useTucksAwayOnScroll();

  if (pathname !== '/') return null;

  return (
    <>
      {/* Without scripting the bar can never be told where the page is, so
          leave it where the design puts it rather than tucked out of sight. */}
      <noscript>
        <style>{'.latest-post-bar{transform:none}'}</style>
      </noscript>

      {/* A tucked-away bar still holds its 58px slot in the flow, and the page
          behind it is white. Paint that slot with the same gradient so the
          reader never catches a white band as the bar slides back out. */}
      <div aria-hidden className="relative h-0">
        <div
          className={classNames(
            'absolute inset-x-0 top-0 border-b-2 border-[#E0EAF0]',
            BAR_HEIGHT
          )}
          style={{ backgroundImage: BAR_GRADIENT }}
        />
      </div>

      <Link
        href={`/blog/${slug}` as Route}
        className={classNames([
          'latest-post-bar group sticky isolate z-10 flex w-full items-center overflow-hidden border-b-2 border-[#E0EAF0] text-black ease-out',
          BAR_HEIGHT,
          NAV_OFFSET,
          // Comes back faster than it leaves — the quicker it covers its slot,
          // the less of the seam behind it there is to notice. Focus pulls it
          // back too, so keyboard users never land on a link behind the nav.
          hidden
            ? '-translate-y-full duration-300 focus-visible:translate-y-0'
            : 'duration-150',
          settled ? 'transition-transform' : 'transition-none',
          'motion-reduce:transition-none',
        ])}
        style={{ backgroundImage: BAR_GRADIENT }}
      >
        {/* The gradient runs edge to edge; everything else — copy, button and the
          texture anchored to its right edge — stays on the navigation's own
          measure, as in the design. */}
        <span className="max-w-view relative mx-auto flex w-full items-center gap-2 self-stretch overflow-hidden px-4 sm:px-8">
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 hidden w-[586px] bg-no-repeat mix-blend-color-burn transition-transform duration-300 ease-out group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100 md:block"
            style={{
              backgroundImage: `url(${flowBackground.src})`,
              backgroundSize: '100% 100%',
            }}
          />

          <span className="text-m relative flex min-w-0 flex-1 items-center gap-2 whitespace-nowrap">
            <span className="min-w-0 truncate font-semibold md:shrink-0">
              “{title}”
            </span>
            <span className="hidden min-w-0 flex-1 truncate font-normal md:block">
              Read the latest article from Primary Informatics Labs and Research
              (PILR)
            </span>
          </span>

          <span
            aria-hidden
            className="relative flex size-8 shrink-0 items-center justify-center rounded-full border border-[#707278] shadow-sm transition-colors group-hover:bg-white/40"
          >
            <ArrowUpRight />
          </span>
        </span>
      </Link>
    </>
  );
};

export default LatestPostBar;
